// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Drawer" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '../../lib/utils';

// ─── Drawer (root) ────────────────────────────────────────────────────────────

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
Drawer.displayName = 'Drawer';

// ─── DrawerTrigger ────────────────────────────────────────────────────────────

const DrawerTrigger = DrawerPrimitive.Trigger;
DrawerTrigger.displayName = 'DrawerTrigger';

// ─── DrawerPortal ─────────────────────────────────────────────────────────────

const DrawerPortal = DrawerPrimitive.Portal;

// ─── DrawerClose ──────────────────────────────────────────────────────────────

const DrawerClose = DrawerPrimitive.Close;
DrawerClose.displayName = 'DrawerClose';

// ─── DrawerOverlay ────────────────────────────────────────────────────────────

const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-foreground/20 backdrop-blur-[3px]', className)}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

// ─── DrawerContent ────────────────────────────────────────────────────────────

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[var(--card-radius,var(--radius))] border border-border bg-background',
        className,
      )}
      {...props}
    >
      <div className="mx-auto mt-4 h-2 w-24 rounded-full bg-muted" />
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

// ─── DrawerHeader ─────────────────────────────────────────────────────────────

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

// ─── DrawerFooter ─────────────────────────────────────────────────────────────

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

// ─── DrawerTitle ──────────────────────────────────────────────────────────────

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight text-foreground', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

// ─── DrawerDescription ────────────────────────────────────────────────────────

const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
